import { Box, Flex, Grid, Progress, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineAutoFireButton } from 'components/machine/buttons/auto-fire';
import {
  ActiveConnectionActionGroup,
  MachineConnectionTrigger,
} from 'components/main/sidebar/footer/connection-trigger';
import { AimingContext } from 'contexts/aiming.context';
import { MachineButton } from 'contexts/firing/button';
import { FiringContext } from 'contexts/firing/context';
import { MachineContext } from 'contexts/machine.context';
import { TrainingContext } from 'contexts/training.context';
import { isFuture, parseISO } from 'date-fns';
import { IgnoreAutoFireMode } from 'enums/firing';
import { PTStep, ProgressDirection } from 'enums/training.enums';
import { t } from 'i18next';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { FiringMode } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFireResponseMsg } from 'lib_ts/interfaces/machine-msg/i-fire-response';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clamp } from 'lodash';
import { FC, useContext, useEffect, useMemo } from 'react';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const COMPONENT_NAME = 'PTStatusBar';

const BTN_CLASS = 'btn-block';

interface IProps {
  step: PTStep;

  iteration: number;
  shots: IMachineShot[];
  totalShots: number;
  isLastPitch: boolean;

  beforeFire?: (mode: FiringMode, isReady: boolean) => void;
  changePitch: (delta: ProgressDirection) => void;
  onFinish?: () => void;
  onFailedToFire?: () => void;
}

const PTStatusBarButton: FC<IProps> = (props) => {
  const { pitchTraining } = useContext(AimingContext);
  const { mode, startDate, resetMsgs, setStartDate } =
    useContext(TrainingContext);
  const { autoFire, lastMSHash } = useContext(MachineContext);
  const tms = useTrainedMsStore((s) => s.tmsDict[pitchTraining?.bs_hash ?? '']);

  if (!lastMSHash || props.step !== PTStep.Complete) {
    return (
      <MachineButton
        identifier="PTStatusBarMachineButton"
        as="dialog-button"
        className="btn-block"
        fireData={{
          mode: autoFire ? 'auto' : 'manual',
          trigger: 'click',
          training: true,
          training_mode: mode,
          hitter_id: undefined,
          tags: 'PRESET',
        }}
        sendData={{
          skipPreview: true,
          trigger: COMPONENT_NAME,
          aimingShots: props.shots,
        }}
        trainingShots={props.shots}
        beforeFire={(isReady) => {
          if (!isReady) {
            return;
          }

          resetMsgs();
        }}
        afterFire={(result) => {
          if (startDate) {
            // a date was already set
            return;
          }

          if (!result.success) {
            // fire failed
            return;
          }

          if (!result.event) {
            // fire event wasn't created
            return;
          }

          // mark the start date for shot queries
          setStartDate(parseISO(result.event._created));
        }}
      />
    );
  }

  if (!props.isLastPitch) {
    // while auto-firing, the app will automatically change pitch for you
    const label =
      props.step === PTStep.Complete
        ? autoFire
          ? 'common.loading'
          : 'common.next'
        : 'common.skip';

    return (
      <DialogButton
        disabled={autoFire}
        className={BTN_CLASS}
        onClick={() => {
          props.changePitch(ProgressDirection.Next);
        }}
        label={label}
      />
    );
  }

  return (
    <DialogButton
      className={BTN_CLASS}
      color={tms?._mx_trained ? RADIX.COLOR.SUCCESS : undefined}
      onClick={() => {
        props.onFinish?.();
      }}
      label="tr.finish"
    />
  );
};

export const PTStatusBar: FC<IProps> = (props) => {
  const { loadingUntil } = useContext(AimingContext);
  const { lastBallCount } = useContext(MachineContext);
  const { safeSetIgnoreAf } = useContext(FiringContext);
  const { failures, setLiveView } = useContext(TrainingContext);

  // at (re-)mount, check if we should pause auto-fire
  useEffect(
    () => {
      const previouslyFailed = failures > 0;

      if (previouslyFailed) {
        // we've previously failed and already surfaced the callout, so don't stop the user anymore
        return;
      }

      safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);
    },
    [
      // don't re-trigger this effect whenever failures changes
    ]
  );

  const safeProgress = clamp(
    props.shots.length / (props.totalShots || 1),
    0,
    1
  );

  const description = useMemo(() => {
    switch (props.step) {
      case PTStep.Complete: {
        return 'common.pitch-trained-successfully';
      }

      case PTStep.Firing:
      default: {
        if (isFuture(loadingUntil)) {
          return 'common.preparing-to-fire';
        }

        if (lastBallCount !== 1) {
          return 'common.no-ball-detected';
        }

        return 'common.preparing-to-fire';
      }
    }
  }, [props.step, loadingUntil, lastBallCount]);

  // listen for failed fires to prompt user to click fire again
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: IFireResponseMsg = event.detail;

      if (!data) {
        NotifyHelper.error({
          message_md: `Empty fire response payload. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
        return;
      }

      if (data.status) {
        // do nothing, this is the happy path
        return;
      }

      console.error('Failed to fire', data);

      const message = data.message ?? 'Failed to fire, reason unknown';

      NotifyHelper.error({
        message_md: message,
        inbox: true,
      });

      props.onFailedToFire?.();

      // have user manually click Fire again
      safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);
    };

    WebSocketHelper.on(WsMsgType.M2U_FireResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_FireResponse, callback);
    };
  }, [props.onFailedToFire, props.onFailedToFire, safeSetIgnoreAf]);

  return (
    <ErrorBoundary identifier="PTStatusBar">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        {/* R2F, Waiting, etc... | Iteration # Shot X/Y */}
        <Flex gap={RADIX.FLEX.GAP.SM} justify="between" align="center">
          <Box>
            <Text truncate>{t(description)}</Text>
          </Box>
          <Box>
            {`${t('common.iteration')} ${props.iteration} · ${t(
              'common.shot'
            )} ${clamp(props.shots.length, 1, props.totalShots)}/${Math.max(
              props.totalShots,
              1
            )}`}
          </Box>
        </Flex>

        <Progress value={safeProgress * 100} />

        <Flex gap={RADIX.FLEX.GAP.SM}>
          <MachineConnectionTrigger
            activeConnectionActionGroups={[
              ActiveConnectionActionGroup.Controls,
              ActiveConnectionActionGroup.Diagnostics,
            ]}
            onEnableLiveView={() => setLiveView((prev) => !prev)}
            as="icon"
            machineInMenu
          />

          <Grid flexGrow="1" columns="2" gap={RADIX.FLEX.GAP.SM}>
            <Box>
              <MachineAutoFireButton
                as="dialog-button"
                className={BTN_CLASS}
                beforeToggleFn={(newValue) =>
                  safeSetIgnoreAf(
                    newValue
                      ? IgnoreAutoFireMode.Temporary
                      : IgnoreAutoFireMode.Disabled
                  )
                }
              />
            </Box>

            <Box>
              <PTStatusBarButton {...props} />
            </Box>
          </Grid>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
